/*
 * custom: load-more — hybrid lazy-load / Load more for collection pages.
 *
 * Behavior (see collection-load-more-spec.md):
 * - Lazy-loads the next page when the sentinel nears the viewport, for
 *   `lazyBatches` batches, then shows the Load more button; a click
 *   loads one page and re-arms lazy loading.
 * - history.replaceState keeps ?page=N current (replace, not push, so
 *   the back button leaves the collection in one step).
 * - The page number and scroll offset go to sessionStorage, so the back
 *   button restores the shopper's exact place even without the
 *   back/forward cache; the grid itself is rebuilt by re-fetching.
 * - Dawn's facets re-render replaces #ProductGridContainer's children;
 *   a MutationObserver re-initializes state when that happens.
 *
 * Portability: keep every Dawn-specific selector in CONFIG.
 */
(() => {
  'use strict';

  const CONFIG = {
    container: '#ProductGridContainer',
    grid: '#product-grid',
    wrapper: '[data-custom-load-more]',
    sentinel: '[data-load-more-sentinel]',
    button: '[data-load-more-button]',
    label: '[data-load-more-label]',
    status: '[data-load-more-status]',
    spinner: '.loading__spinner',
    pagination: '.pagination-wrapper',
    countIds: ['ProductCount', 'ProductCountDesktop'],
    cardLink: '.card__heading a, a.full-unstyled-link',
    rootMargin: '800px 0px',
    storagePrefix: 'custom-load-more:',
    storageMaxAgeMs: 30 * 60 * 1000,
    restoreMaxPages: 10, // rebuild ceiling on return; deeper sessions clamp
  };

  let state = null;
  let io = null;
  let rerenderObserver = null;

  const qs = (sel, root) => (root || document).querySelector(sel);


  function collectionKey() {
    const params = new URLSearchParams(location.search);
    params.delete('page');
    params.sort();
    return CONFIG.storagePrefix + location.pathname + '?' + params.toString();
  }

  function pageUrl(page) {
    const url = new URL(location.href);
    if (page > 1) url.searchParams.set('page', page);
    else url.searchParams.delete('page');
    return url;
  }

  function readState() {
    const wrapper = qs(CONFIG.wrapper);
    const grid = qs(CONFIG.grid);
    if (!wrapper || !grid) return null;
    const d = wrapper.dataset;
    return {
      wrapper,
      grid,
      sentinel: qs(CONFIG.sentinel, wrapper),
      button: qs(CONFIG.button, wrapper),
      label: qs(CONFIG.label, wrapper),
      status: qs(CONFIG.status, wrapper),
      lazyBatches: readBatches(d),
      perPage: parseInt(d.perPage, 10) || 36,
      currentPage: parseInt(d.currentPage, 10) || 1,
      totalPages: readTotalPages() || parseInt(d.totalPages, 10) || 1,
      totalProducts: parseInt(d.totalProducts, 10) || 0,
      batchesSinceButton: 0,
      fetching: false,
    };
  }

  // Two tiers, because the shopper scrolls rows, not products. Measured
  // on this grid: mobile (2 columns, 422px row pitch) shows 3.8 products
  // per screen, desktop (4 columns, but 616px rows — wider cards mean
  // taller images) shows 5.8. So four columns buys ~1.5x, not 2x, and
  // 108 on desktop is the same ~19 screens of scrolling as 72 on mobile.
  // Re-measure the row pitch if columns_desktop or the card aspect ratio
  // changes; the column count alone does not give the equivalence.
  //
  // Read once at init rather than on resize: retuning mid-session would
  // move the button while the shopper is scrolling toward it.
  function readBatches(d) {
    const raw = matchMedia('(min-width: 990px)').matches ? d.lazyBatchesDesktop : d.lazyBatches;
    const n = parseInt(raw, 10);
    return isNaN(n) ? parseInt(d.lazyBatches, 10) || 0 : n;
  }

  // The server-rendered pagination is the source of truth for total
  // pages (last numbered link); it stays in the DOM for crawlers and
  // no-JS shoppers and is hidden by custom-load-more.css.
  function readTotalPages() {
    const nums = [...document.querySelectorAll(CONFIG.pagination + ' a, ' + CONFIG.pagination + ' span')]
      .map((el) => parseInt(el.textContent.trim(), 10))
      .filter((n) => !isNaN(n));
    return nums.length ? Math.max(...nums) : 0;
  }

  function announce(text) {
    if (state.status) state.status.textContent = text;
  }

  function updateCounts() {
    const shown = state.grid.querySelectorAll(':scope > li').length;
    const text = 'Showing ' + shown + ' of ' + state.totalProducts + ' items';
    CONFIG.countIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        const target = el.querySelector('span') || el;
        target.textContent = text;
      }
    });
    announce(text);
  }

  function setBusy(busy, fromButton) {
    state.grid.setAttribute('aria-busy', busy ? 'true' : 'false');
    // Button loads spin inside the (still visible, disabled) button;
    // lazy loads spin in the sentinel area.
    const host = fromButton ? state.button : state.sentinel;
    const spinner = qs(CONFIG.spinner, host);
    if (spinner) spinner.classList.toggle('hidden', !busy);
    if (fromButton) {
      state.button.disabled = busy;
      state.button.classList.toggle('loading', busy);
    }
  }

  async function fetchPage(page) {
    const url = pageUrl(page);
    url.searchParams.set('section_id', state.grid.dataset.id);
    const response = await fetch(url.toString());
    if (!response.ok) throw new Error('HTTP ' + response.status);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const items = [...html.querySelectorAll(CONFIG.grid + ' > li')];
    // Each batch is a fresh render of the section, so forloop.index
    // restarts and the first cards of every page come back eager —
    // aimed at an above-the-fold slot that only exists on page 1. An
    // appended batch is below the fold by definition. The parsed
    // document is inert, so this lands before anything can fetch.
    items.forEach((li) => {
      li.querySelectorAll('img:not([loading])').forEach((img) => {
        img.setAttribute('loading', 'lazy');
      });
    });
    return items;
  }

  async function loadNext(fromButton) {
    if (state.fetching || state.currentPage >= state.totalPages) return;
    state.fetching = true;
    setBusy(true, fromButton);
    const nextPage = state.currentPage + 1;
    try {
      const items = await fetchPage(nextPage);
      const firstNew = items[0];
      items.forEach((li) => state.grid.appendChild(li));
      state.currentPage = nextPage;
      state.batchesSinceButton += 1;
      history.replaceState(history.state, '', pageUrl(nextPage));
      updateCounts();
      saveSession();
      if (fromButton && firstNew) {
        const link = firstNew.querySelector(CONFIG.cardLink);
        if (link) {
          firstNew.classList.add('custom-scroll-offset');
          link.focus();
        }
      }
      state.label.textContent = state.label.dataset.label;
    } catch (error) {
      console.warn('custom-load-more: fetch failed', error);
      state.label.textContent = "Couldn't load more — try again";
      state.batchesSinceButton = state.lazyBatches; // force the button
    }
    state.fetching = false;
    setBusy(false, fromButton);
    render();
  }

  function render() {
    const exhausted = state.currentPage >= state.totalPages;
    const needButton = !exhausted && state.batchesSinceButton >= state.lazyBatches;
    state.button.hidden = !needButton;
    state.sentinel.hidden = exhausted;
    if (io) io.disconnect();
    if (!exhausted && !needButton) {
      io = new IntersectionObserver(
        (entries) => entries.forEach((e) => e.isIntersecting && loadNext(false)),
        { rootMargin: CONFIG.rootMargin }
      );
      io.observe(state.sentinel);
    }
  }

  /* ---- session restore (spec 4.6) ---------------------------------- */

  // Position only — no grid markup. Storing the HTML meant a ~1MB
  // JSON.stringify on every batch (measured: 13ms on desktop, several
  // times that on a phone, right while the shopper is scrolling), and a
  // 240-card cap that dropped the earliest products, so a deep session
  // restored a SHORTER page and the saved offset overshot. The page
  // number is enough: the grid is rebuilt by re-fetching on return.
  function saveSession() {
    try {
      sessionStorage.setItem(
        collectionKey(),
        JSON.stringify({
          page: state.currentPage,
          batchesSinceButton: state.batchesSinceButton,
          scrollY: window.scrollY,
          savedAt: Date.now(),
        })
      );
    } catch (e) {}
  }

  // Returning to ?page=N renders page N on its own, so pages 1..N-1 are
  // missing from the top of the grid. Fetch them in parallel (one round
  // trip, not N) and prepend in order, so the geometry matches what the
  // shopper left and the saved offset lands on the same row.
  async function refill(targetPage) {
    const first = Math.max(1, targetPage - CONFIG.restoreMaxPages);
    const pages = [];
    for (let p = first; p < targetPage; p++) pages.push(p);
    if (!pages.length) return;
    const batches = await Promise.all(pages.map((p) => fetchPage(p).catch(() => [])));
    const frag = document.createDocumentFragment();
    batches.forEach((items) => items.forEach((li) => frag.appendChild(li)));
    state.grid.insertBefore(frag, state.grid.firstChild);
    updateCounts();
  }

  // Clamped to what actually exists: if the rebuild came up short (a
  // failed fetch, or a session deeper than restoreMaxPages) the shopper
  // lands at the end of the grid rather than past it.
  function applyScroll(y) {
    const target = () =>
      Math.min(y, Math.max(0, document.documentElement.scrollHeight - window.innerHeight));
    window.scrollTo(0, target());
    // iOS Safari can clobber an early scrollTo during load with its own
    // restoration; re-assert until it sticks.
    [100, 400, 900].forEach((delay) => {
      setTimeout(() => {
        const t = target();
        if (Math.abs(window.scrollY - t) > 80) window.scrollTo(0, t);
      }, delay);
    });
  }

  function tryRestore() {
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(collectionKey()));
    } catch (e) {
      saved = null;
    }
    if (!saved || Date.now() - saved.savedAt > CONFIG.storageMaxAgeMs) {
      // Hand scroll restoration back to the browser. `manual` is stored
      // on the history ENTRY, not the page load, so one earlier restore
      // leaves this collection's entry manual for the rest of the tab's
      // session. Any later return without a payload — expired after 30
      // minutes, cleared, saved-in-another-tab, or a departure that
      // never fired pagehide — then had nobody restoring and landed at
      // the top of the collection. Failing back to native is the worst
      // case now, not a jump to the top.
      try {
        history.scrollRestoration = 'auto';
      } catch (e) {}
      return false;
    }
    // Only now do we own scroll restoration; with no (fresh) entry the
    // browser's native behavior stays in charge.
    history.scrollRestoration = 'manual';
    sessionStorage.removeItem(collectionKey());
    state.batchesSinceButton = saved.batchesSinceButton || 0;
    if (saved.page > 1) {
      // No pre-scroll here: the page is one batch tall until the rebuild
      // lands, so scrolling first would jump to its end and then jump
      // again. Scroll once, when the grid is the right height.
      state.currentPage = Math.max(state.currentPage, saved.page);
      refill(saved.page)
        .catch(() => {})
        .then(() => {
          render();
          applyScroll(saved.scrollY);
        });
    } else {
      applyScroll(saved.scrollY);
    }
    return true;
  }

  /* ---- init & re-init ---------------------------------------------- */

  function init(restoring) {
    state = readState();
    if (!state) return;
    // Stamps the container so the stylesheet can hide the numbered
    // pagination — only when this script actually runs (the theme
    // hardcodes html.js, so the no-js class can't gate it). The stamp
    // survives facets re-renders (children replaced, container kept).
    const container = qs(CONFIG.container);
    if (container) container.classList.add('custom-load-more-active');
    state.label.dataset.label = state.label.textContent.trim();
    state.button.addEventListener('click', () => {
      // Keep the button visible while its spinner runs; render()
      // hides it after the batch lands.
      state.batchesSinceButton = 0;
      loadNext(true);
    });
    if (restoring) tryRestore();
    render();
  }

  function watchRerenders() {
    const container = qs(CONFIG.container);
    if (!container) return;
    rerenderObserver = new MutationObserver(() => {
      // Dawn replaced the grid (filter/sort). Fresh markup includes a
      // fresh wrapper (it renders inside the container), so re-read
      // everything; Dawn already stripped ?page from the URL.
      if (state && !state.grid.isConnected) {
        init(false);
        // Applying a filter/sort should present the refreshed results
        // from the top of the grid (scroll-margin-top keeps it clear
        // of the scroll-in bar), and cancels any drawer scroll-pin.
        document.dispatchEvent(new CustomEvent('custom:grid-rerendered'));
        container.scrollIntoView({ block: 'start' });
      }
    });
    rerenderObserver.observe(container, { childList: true, subtree: true });
  }

  if (!qs(CONFIG.wrapper)) return;
  init(true);
  watchRerenders();
  // Save on every departure, whatever the page count — scrollY-only
  // restores cover first-page browsing (the common case).
  addEventListener('pagehide', () => state && saveSession());
  addEventListener('pageshow', (event) => {
    if (!state) return;
    if (event.persisted) {
      // The back/forward cache restored the DOM (lazy-loaded pages
      // included) — but iOS Safari restores the DOM and then leaves
      // the scroll at 0 (verified on iPhone: persisted=true,
      // scrollY=0 despite a native restore being due). We saved the
      // position on pagehide, so re-apply just the scroll.
      let saved = null;
      try {
        saved = JSON.parse(sessionStorage.getItem(collectionKey()));
      } catch (e) {
        return;
      }
      if (!saved || Date.now() - saved.savedAt > CONFIG.storageMaxAgeMs) {
        return;
      }
      sessionStorage.removeItem(collectionKey());
      // The DOM came back with it, so there is nothing to rebuild —
      // only the scroll position to re-apply.
      applyScroll(saved.scrollY);
      return;
    }
    tryRestore() && render();
  });
})();
